/**
 * FontPreview — fetches an authenticated PNG from /v1/admin/fonts/{id}/preview
 * and renders it as a blob URL.
 *
 * <img src> tags cannot carry the Authorization header that apiClient injects,
 * so we fetch the preview via apiClient (responseType=blob) and create a
 * local object URL. Previews are deduped + cached via TanStack Query.
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fontsApi } from '@/lib/api/fonts'

interface FontPreviewProps {
  fontId: number
  text: string
  size?: number
  alt?: string
  className?: string
}

export function FontPreview({
  fontId,
  text,
  size = 48,
  alt,
  className,
}: FontPreviewProps) {
  const { data: blob, isLoading, isError } = useQuery({
    queryKey: ['fonts', 'preview', fontId, text, size],
    queryFn: () => fontsApi.previewBlob(fontId, text, size),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
  })

  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!blob) {
      setObjectUrl(null)
      return
    }
    const url = URL.createObjectURL(blob)
    setObjectUrl(url)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [blob])

  if (isLoading) {
    return <span className={`inline-block text-xs text-gray-400 ${className ?? ''}`}>...</span>
  }
  if (isError || !objectUrl) {
    return <span className={`inline-block text-xs text-red-500 ${className ?? ''}`}>预览失败</span>
  }
  return <img src={objectUrl} alt={alt ?? ''} className={className} />
}
